import { useState, useEffect } from "react";
import {
  Mic,
  Plus,
  Settings,
  Trash2,
  Edit3,
  Save,
  X,
  Play,
  Pause,
  Volume2,
  Brain,
  Zap,
  List,
  MessageSquare,
  BarChart3,
  AlertCircle,
  CheckCircle,
  Clock,
  TestTube,
} from "lucide-react";
import medicalDB from "../services/database";
import VoiceCommandTester from "./VoiceCommandTester";

const COMMAND_TYPES = {
  simple: { icon: Zap, label: "Simple", description: "Single action command" },
  sequence: {
    icon: List,
    label: "Sequence",
    description: "Multiple actions in order",
  },
  parameterized: {
    icon: Settings,
    label: "Parameterized",
    description: "Commands with variables",
  },
  natural: {
    icon: MessageSquare,
    label: "Natural",
    description: "Conversational commands",
  },
};

const MEMORY_CATEGORIES = {
  guidelines: { icon: AlertCircle, label: "Guidelines", color: "text-red-600" },
  preferences: { icon: Settings, label: "Preferences", color: "text-blue-600" },
  protocols: { icon: List, label: "Protocols", color: "text-green-600" },
  notes: { icon: MessageSquare, label: "Notes", color: "text-purple-600" },
};

function VoiceCommands() {
  const [activeTab, setActiveTab] = useState("commands");
  const [voiceCommands, setVoiceCommands] = useState([]);
  const [doctorMemory, setDoctorMemory] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState("command");
  const [loading, setLoading] = useState(true);
  const [costInfo, setCostInfo] = useState({
    sessionsCount: 0,
    audioMinutes: 0,
    functionCalls: 0,
    currentSessionDuration: 0,
  });

  // Mock doctor ID - in real app this would come from authentication
  const doctorId = "default-doctor";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [commands, memory] = await Promise.all([
        medicalDB.getDoctorVoiceCommands(doctorId),
        medicalDB.getAllDoctorMemory(doctorId),
      ]);

      setVoiceCommands(commands);
      setDoctorMemory(memory);

      // Seed default memory if none exists
      if (memory.length === 0) {
        await medicalDB.seedDoctorMemory(doctorId);
        const newMemory = await medicalDB.getAllDoctorMemory(doctorId);
        setDoctorMemory(newMemory);
      }
    } catch (error) {
      console.error("Error loading voice commands data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCommand = () => {
    setFormType("command");
    setEditingItem(null);
    setShowForm(true);
  };

  const handleCreateMemory = () => {
    setFormType("memory");
    setEditingItem(null);
    setShowForm(true);
  };

  const handleEditItem = (item, type) => {
    setFormType(type);
    setEditingItem(item);
    setShowForm(true);
  };

  const handleDeleteItem = async (id, type) => {
    if (!confirm(`Are you sure you want to delete this ${type}?`)) return;

    try {
      if (type === "command") {
        await medicalDB.deleteVoiceCommand(id);
        setVoiceCommands((prev) => prev.filter((cmd) => cmd.id !== id));
      } else {
        await medicalDB.deleteDoctorMemory(id);
        setDoctorMemory((prev) => prev.filter((mem) => mem.id !== id));
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      alert(`Failed to delete ${type}. Please try again.`);
    }
  };

  const handleToggleActive = async (id, isActive, type) => {
    try {
      if (type === "command") {
        await medicalDB.updateVoiceCommand(id, { isActive: !isActive });
        setVoiceCommands((prev) =>
          prev.map((cmd) =>
            cmd.id === id ? { ...cmd, isActive: !isActive } : cmd
          )
        );
      } else {
        await medicalDB.updateDoctorMemory(id, { isActive: !isActive });
        setDoctorMemory((prev) =>
          prev.map((mem) =>
            mem.id === id ? { ...mem, isActive: !isActive } : mem
          )
        );
      }
    } catch (error) {
      console.error(`Error toggling ${type} status:`, error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Voice Commands</h1>
          <p className="text-gray-600 mt-1">
            Manage your custom voice commands and AI memory
          </p>
        </div>

        {/* Cost Monitor */}
        <div className="bg-white rounded-lg shadow-sm border p-4 min-w-[200px]">
          <div className="flex items-center space-x-2 mb-2">
            <BarChart3 size={16} className="text-green-600" />
            <span className="text-sm font-medium text-gray-700">
              Usage Stats
            </span>
          </div>
          <div className="text-xs text-gray-600 space-y-1">
            <div>Sessions: {costInfo.sessionsCount}</div>
            <div>Audio: {costInfo.audioMinutes.toFixed(1)}min</div>
            <div>Functions: {costInfo.functionCalls}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("commands")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "commands"
                ? "border-medical-blue text-medical-blue"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center space-x-2">
              <Mic size={16} />
              <span>Voice Commands</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {voiceCommands.length}
              </span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab("memory")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "memory"
                ? "border-medical-blue text-medical-blue"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center space-x-2">
              <Brain size={16} />
              <span>Doctor Memory</span>
              <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                {doctorMemory.length}
              </span>
            </div>
          </button>

          <button
            onClick={() => setActiveTab("testing")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "testing"
                ? "border-medical-blue text-medical-blue"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <div className="flex items-center space-x-2">
              <TestTube size={16} />
              <span>Voice Testing</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "commands" && (
        <CommandsTab
          commands={voiceCommands}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          onToggleActive={handleToggleActive}
          onCreate={handleCreateCommand}
        />
      )}

      {activeTab === "memory" && (
        <MemoryTab
          memory={doctorMemory}
          onEdit={handleEditItem}
          onDelete={handleDeleteItem}
          onToggleActive={handleToggleActive}
          onCreate={handleCreateMemory}
        />
      )}

      {activeTab === "testing" && (
        <div className="space-y-6">
          <VoiceCommandTester />
        </div>
      )}

      {/* Forms Modal */}
      {showForm && (
        <FormModal
          type={formType}
          item={editingItem}
          doctorId={doctorId}
          onClose={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingItem(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function CommandsTab({ commands, onEdit, onDelete, onToggleActive, onCreate }) {
  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {commands.filter((cmd) => cmd.isActive).length} active commands
        </div>
        <button
          onClick={onCreate}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={16} />
          <span>Add Command</span>
        </button>
      </div>

      {/* Commands List */}
      {commands.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Mic size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No voice commands yet
          </h3>
          <p className="text-gray-600 mb-6">
            Create your first custom voice command to get started
          </p>
          <button onClick={onCreate} className="btn-primary">
            Create Command
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {commands.map((command) => (
            <CommandCard
              key={command.id}
              command={command}
              onEdit={(cmd) => onEdit(cmd, "command")}
              onDelete={(id) => onDelete(id, "command")}
              onToggleActive={(id, isActive) =>
                onToggleActive(id, isActive, "command")
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CommandCard({ command, onEdit, onDelete, onToggleActive }) {
  const TypeIcon = COMMAND_TYPES[command.type]?.icon || Zap;

  return (
    <div
      className={`bg-white rounded-lg border shadow-sm p-6 ${
        !command.isActive ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <TypeIcon size={20} className="text-medical-blue" />
            <h3 className="text-lg font-semibold text-gray-900">
              {command.name}
            </h3>
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                command.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {command.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="mb-3">
            <span className="text-sm text-gray-600">Triggers:</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {command.triggers.map((trigger, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-50 text-blue-700 text-sm rounded"
                >
                  "{trigger}"
                </span>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <span className="text-sm text-gray-600">Actions:</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {command.actions.map((action, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-gray-50 text-gray-700 text-sm rounded"
                >
                  {action.type || action}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span>Type: {COMMAND_TYPES[command.type]?.label}</span>
            <span>Used: {command.usageCount || 0} times</span>
            <span>
              Updated: {new Date(command.updatedAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onToggleActive(command.id, command.isActive)}
            className={`p-2 rounded-lg transition-colors ${
              command.isActive
                ? "text-green-600 hover:bg-green-50"
                : "text-gray-400 hover:bg-gray-50"
            }`}
            title={command.isActive ? "Deactivate" : "Activate"}
          >
            {command.isActive ? <CheckCircle size={16} /> : <Clock size={16} />}
          </button>

          <button
            onClick={() => onEdit(command)}
            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
            title="Edit"
          >
            <Edit3 size={16} />
          </button>

          <button
            onClick={() => onDelete(command.id)}
            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MemoryTab({ memory, onEdit, onDelete, onToggleActive, onCreate }) {
  const groupedMemory = memory.reduce((groups, item) => {
    const category = item.category;
    if (!groups[category]) groups[category] = [];
    groups[category].push(item);
    return groups;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {memory.filter((mem) => mem.isActive).length} active memory entries
        </div>
        <button
          onClick={onCreate}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={16} />
          <span>Add Memory</span>
        </button>
      </div>

      {/* Memory Categories */}
      {Object.keys(groupedMemory).length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Brain size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No memory entries yet
          </h3>
          <p className="text-gray-600 mb-6">
            Add guidelines, preferences, and protocols for better AI assistance
          </p>
          <button onClick={onCreate} className="btn-primary">
            Add Memory
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedMemory).map(([category, items]) => (
            <div key={category} className="space-y-3">
              <div className="flex items-center space-x-2">
                {MEMORY_CATEGORIES[category] &&
                  (() => {
                    const IconComponent = MEMORY_CATEGORIES[category].icon;
                    return (
                      <IconComponent
                        size={20}
                        className={MEMORY_CATEGORIES[category].color}
                      />
                    );
                  })()}
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {category}
                </h3>
                <span className="text-sm text-gray-500">({items.length})</span>
              </div>

              <div className="grid gap-3">
                {items.map((item) => (
                  <MemoryCard
                    key={item.id}
                    memory={item}
                    onEdit={(mem) => onEdit(mem, "memory")}
                    onDelete={(id) => onDelete(id, "memory")}
                    onToggleActive={(id, isActive) =>
                      onToggleActive(id, isActive, "memory")
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemoryCard({ memory, onEdit, onDelete, onToggleActive }) {
  return (
    <div
      className={`bg-white rounded-lg border shadow-sm p-4 ${
        !memory.isActive ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <h4 className="font-medium text-gray-900">{memory.title}</h4>
            <span
              className={`px-2 py-0.5 text-xs rounded-full ${
                memory.isActive
                  ? "bg-green-100 text-green-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {memory.isActive ? "Active" : "Inactive"}
            </span>
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
              Priority {memory.priority}
            </span>
          </div>

          <p className="text-gray-600 text-sm mb-2">{memory.content}</p>

          <div className="text-xs text-gray-500">
            Updated: {new Date(memory.updatedAt).toLocaleDateString()}
          </div>
        </div>

        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={() => onToggleActive(memory.id, memory.isActive)}
            className={`p-1.5 rounded transition-colors ${
              memory.isActive
                ? "text-green-600 hover:bg-green-50"
                : "text-gray-400 hover:bg-gray-50"
            }`}
            title={memory.isActive ? "Deactivate" : "Activate"}
          >
            {memory.isActive ? <CheckCircle size={14} /> : <Clock size={14} />}
          </button>

          <button
            onClick={() => onEdit(memory)}
            className="p-1.5 rounded text-blue-600 hover:bg-blue-50 transition-colors"
            title="Edit"
          >
            <Edit3 size={14} />
          </button>

          <button
            onClick={() => onDelete(memory.id)}
            className="p-1.5 rounded text-red-600 hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function FormModal({ type, item, doctorId, onClose, onSave }) {
  const [formData, setFormData] = useState(() => {
    if (type === "command") {
      return {
        name: item?.name || "",
        type: item?.type || "simple",
        triggers: item?.triggers?.join(", ") || "",
        actions: item?.actions?.join(", ") || "",
        parameters: JSON.stringify(item?.parameters || {}, null, 2),
        isActive: item?.isActive !== false,
      };
    } else {
      return {
        title: item?.title || "",
        category: item?.category || "guidelines",
        content: item?.content || "",
        priority: item?.priority || 1,
        isActive: item?.isActive !== false,
      };
    }
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (type === "command") {
        const commandData = {
          doctorId,
          name: formData.name,
          type: formData.type,
          triggers: formData.triggers
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          actions: formData.actions
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean),
          parameters: JSON.parse(formData.parameters || "{}"),
          isActive: formData.isActive,
        };

        if (item) {
          await medicalDB.updateVoiceCommand(item.id, commandData);
        } else {
          await medicalDB.createVoiceCommand(commandData);
        }
      } else {
        const memoryData = {
          doctorId,
          title: formData.title,
          category: formData.category,
          content: formData.content,
          priority: parseInt(formData.priority),
          isActive: formData.isActive,
        };

        if (item) {
          await medicalDB.updateDoctorMemory(item.id, memoryData);
        } else {
          await medicalDB.createDoctorMemory(memoryData);
        }
      }

      onSave();
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
      alert(`Failed to save ${type}. Please check your input and try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {item ? "Edit" : "Create"}{" "}
              {type === "command" ? "Voice Command" : "Memory Entry"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {type === "command" ? (
              <CommandForm formData={formData} setFormData={setFormData} />
            ) : (
              <MemoryForm formData={formData} setFormData={setFormData} />
            )}

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex items-center space-x-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save size={16} />
                )}
                <span>{saving ? "Saving..." : "Save"}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CommandForm({ formData, setFormData }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Command Name
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-field"
          placeholder="e.g., Add Patient, Record Vitals"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Command Type
        </label>
        <select
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          className="input-field"
        >
          {Object.entries(COMMAND_TYPES).map(
            ([key, { label, description }]) => (
              <option key={key} value={key}>
                {label} - {description}
              </option>
            )
          )}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Trigger Phrases (comma-separated)
        </label>
        <input
          type="text"
          value={formData.triggers}
          onChange={(e) =>
            setFormData({ ...formData, triggers: e.target.value })
          }
          className="input-field"
          placeholder="add patient, new patient, create patient"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Enter phrases that will trigger this command
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Actions (comma-separated)
        </label>
        <input
          type="text"
          value={formData.actions}
          onChange={(e) =>
            setFormData({ ...formData, actions: e.target.value })
          }
          className="input-field"
          placeholder="add-patient, navigate-to-form"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Actions to execute when this command is triggered
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Parameters (JSON)
        </label>
        <textarea
          value={formData.parameters}
          onChange={(e) =>
            setFormData({ ...formData, parameters: e.target.value })
          }
          className="input-field h-24 font-mono text-sm"
          placeholder='{"example": "value"}'
        />
        <p className="text-xs text-gray-500 mt-1">
          JSON object with command parameters
        </p>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="command-active"
          checked={formData.isActive}
          onChange={(e) =>
            setFormData({ ...formData, isActive: e.target.checked })
          }
          className="h-4 w-4 text-medical-blue border-gray-300 rounded focus:ring-medical-blue"
        />
        <label htmlFor="command-active" className="ml-2 text-sm text-gray-700">
          Active (command will respond to voice triggers)
        </label>
      </div>
    </>
  );
}

function MemoryForm({ formData, setFormData }) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Title
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="input-field"
          placeholder="e.g., HIPAA Guidelines, Preferred Workflow"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category
        </label>
        <select
          value={formData.category}
          onChange={(e) =>
            setFormData({ ...formData, category: e.target.value })
          }
          className="input-field"
        >
          {Object.entries(MEMORY_CATEGORIES).map(([key, { label }]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Content
        </label>
        <textarea
          value={formData.content}
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
          className="input-field h-32"
          placeholder="Enter detailed guidelines, preferences, or notes..."
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Priority (1-5)
        </label>
        <select
          value={formData.priority}
          onChange={(e) =>
            setFormData({ ...formData, priority: e.target.value })
          }
          className="input-field"
        >
          <option value={1}>1 - Low</option>
          <option value={2}>2 - Below Normal</option>
          <option value={3}>3 - Normal</option>
          <option value={4}>4 - High</option>
          <option value={5}>5 - Critical</option>
        </select>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="memory-active"
          checked={formData.isActive}
          onChange={(e) =>
            setFormData({ ...formData, isActive: e.target.checked })
          }
          className="h-4 w-4 text-medical-blue border-gray-300 rounded focus:ring-medical-blue"
        />
        <label htmlFor="memory-active" className="ml-2 text-sm text-gray-700">
          Active (include in AI context)
        </label>
      </div>
    </>
  );
}

export default VoiceCommands;
